import type { FunctionalComponent } from 'preact';
import { useMemo, useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '../store';
import { QuantityControl } from '../components/QuantityControl';
import { SponsorThumbnailCard } from './SponsorVideo';
import { formatFeePreviewInclusions } from '../lib/feePreview';
import {
    getDisplayPrice,
    canUseFeePreviewForTicketsCart,
    sumTicketLinesCents,
    getTicketLineSubtotalCents,
    sumOrderDiscountableAndFixedCents,
    orderTotalAfterCartPromoCents,
} from '../lib/tierCartPricing';
import { formatCurrency } from '../utils/theme';
import { calculateFees } from '../api/client';
import type { FeeStructure } from '../types';
import { tierEligibleForTicketCheckout } from '../lib/feeTierDesignation';
import {
    getEventSaleTimeZone,
    isEventCancelled,
    isEventClosedForSales,
    isEventEnded,
    isFeeStructurePurchasable,
} from '../lib/eventSaleRules';

export const TicketSelection: FunctionalComponent = () => {
    const { state, dispatch } = useStore();
    const { feeStructures, cart, widget, discount, unlockedSponsorTiers, event } = state;
    const [checkoutError, setCheckoutError] = useState<string | null>(null);

    const saleTimeZone = useMemo(() => getEventSaleTimeZone(event), [event]);
    const eventClosed = useMemo(() => isEventClosedForSales(event), [event]);
    const eventEnded = useMemo(() => isEventEnded(event?.event_end_date), [event?.event_end_date]);
    const eventCancelled = useMemo(() => isEventCancelled(event), [event]);

    const ticketTiersForCheckout = useMemo(() => {
        if (!event?.seated_event) return feeStructures;
        return feeStructures.filter(t => tierEligibleForTicketCheckout(t.designation));
    }, [feeStructures, event?.seated_event]);

    const useFeePreviewTotals = canUseFeePreviewForTicketsCart(cart, unlockedSponsorTiers);

    /** List / all-in (fee_preview when at list price) before cart-level promo — not sales-calc subtotal. */
    const prePromoCents = useMemo(() => sumTicketLinesCents(cart, unlockedSponsorTiers), [cart, unlockedSponsorTiers]);

    const orderSplitCents = useMemo(
        () => sumOrderDiscountableAndFixedCents(cart, unlockedSponsorTiers, [], [], null),
        [cart, unlockedSponsorTiers],
    );

    const afterPromoCents = useMemo(
        () => orderTotalAfterCartPromoCents(prePromoCents, orderSplitCents, discount),
        [prePromoCents, orderSplitCents, discount],
    );

    const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);
    const currency = ticketTiersForCheckout[0]?.currency || feeStructures[0]?.currency || 'USD';
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (afterPromoCents <= 0) {
            dispatch({ type: 'CLEAR_FEE_CALCULATION' });
            dispatch({ type: 'SET_CALCULATING_FEES', calculating: false });
            return;
        }

        if (useFeePreviewTotals || discount) {
            dispatch({ type: 'CLEAR_FEE_CALCULATION' });
            dispatch({ type: 'SET_CALCULATING_FEES', calculating: false });
            if (debounceRef.current) clearTimeout(debounceRef.current);
            return;
        }

        dispatch({ type: 'SET_CALCULATING_FEES', calculating: true });

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const eventId = state.event?._id;
            calculateFees(afterPromoCents, currency, 'tickets', undefined, eventId)
                .then(data => dispatch({ type: 'SET_FEE_CALCULATION', feeCalculation: data }))
                .catch(() => dispatch({ type: 'CLEAR_FEE_CALCULATION' }));
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [afterPromoCents, currency, state.event?._id, useFeePreviewTotals, discount, dispatch]);

    const fees = useMemo(() => {
        if (useFeePreviewTotals || discount) return 0;
        if (!state.feeCalculation) return 0;
        if (state.feeCalculation.totals?.total_fees !== undefined) {
            return state.feeCalculation.totals.total_fees;
        }
        if (state.feeCalculation.breakdown?.total_fees !== undefined) {
            return state.feeCalculation.breakdown.total_fees;
        }
        if (state.feeCalculation.total_fees !== undefined) {
            return state.feeCalculation.total_fees;
        }
        return 0;
    }, [state.feeCalculation, useFeePreviewTotals, discount]);

    const tax = useMemo(() => {
        if (useFeePreviewTotals || discount) return 0;
        if (!state.feeCalculation) return 0;
        const t = state.feeCalculation.totals?.tax ?? state.feeCalculation.breakdown?.tax;
        return t ?? 0;
    }, [state.feeCalculation, useFeePreviewTotals, discount]);

    const grandTotalCents = useMemo(() => {
        if (useFeePreviewTotals || discount) {
            return afterPromoCents;
        }
        const apiTotal = state.feeCalculation?.totals?.total_amount ?? state.feeCalculation?.breakdown?.total_amount;
        if (apiTotal !== undefined) return apiTotal;
        return afterPromoCents + fees + tax;
    }, [useFeePreviewTotals, discount, afterPromoCents, state.feeCalculation, fees, tax]);

    function getQty(tierId: string): number {
        return cart.find(c => c.tier._id === tierId)?.quantity || 0;
    }

    function handleContinue() {
        setCheckoutError(null);
        if (eventClosed) {
            setCheckoutError(
                eventCancelled
                    ? 'This event has been cancelled. Tickets are no longer available.'
                    : 'This event has ended. Tickets are no longer available.',
            );
            return;
        }
        const invalidLine = cart.find(c => !isFeeStructurePurchasable(c.tier, saleTimeZone, false));
        if (invalidLine) {
            setCheckoutError('One or more selected tiers are no longer on sale. Update your selection.');
            return;
        }
        dispatch({ type: 'SET_STEP', step: 'contact' });
    }

    const enableSponsorFlow = !!widget?.enable_sponsor_flow;

    return (
        <div>
            {eventClosed && (
                <div
                    class="mw-event-closed-banner"
                    style={{
                        marginBottom: '1rem',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        background: 'var(--mw-surface-container-low, rgba(0,0,0,0.06))',
                        color: 'var(--mw-text-muted)',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                    }}>
                    {eventCancelled
                        ? 'This event has been cancelled. Ticket sales are closed.'
                        : eventEnded
                          ? 'This event has ended. Ticket sales are closed.'
                          : 'Ticket sales are currently unavailable.'}
                </div>
            )}
            <div class="mw-tiers">
                {ticketTiersForCheckout.length === 0 && event?.seated_event ? (
                    <p
                        style={{
                            padding: '1rem',
                            color: 'var(--mw-text-muted)',
                            textAlign: 'center',
                            fontSize: '0.9rem',
                        }}>
                        No general admission tiers are available here. Use the Tables tab if this event sells tables
                        separately.
                    </p>
                ) : null}
                {ticketTiersForCheckout.map(tier => {
                    const onSale = !eventClosed && isFeeStructurePurchasable(tier, saleTimeZone, false);
                    const soldOut = tier.available <= 0;
                    const hasSponsor =
                        enableSponsorFlow && !!tier.sponsor && (!!tier.sponsor_discount || !!tier.sponsur_free_tier);
                    const sponsorUnlocked = !!unlockedSponsorTiers[tier._id];
                    const isFree =
                        hasSponsor &&
                        (!!tier.sponsur_free_tier || (tier.sponsor_discount && tier.sponsor_discount.discount >= 100));
                    const isFreeLocked = isFree && !sponsorUnlocked;

                    const { original, effective, discountLabel } = getDisplayPrice(tier, sponsorUnlocked);
                    const showServerAllIn =
                        !isFreeLocked && tier.fee_preview?.formatted_total && effective > 0 && effective === tier.fee;
                    const qty = getQty(tier._id);
                    const maxQty = Math.min(tier.max_ticket_per_person, tier.available);

                    const tierClass = `mw-tier${qty > 0 ? ' mw-tier--selected' : ''}${soldOut ? ' mw-tier--sold-out' : ''}${!onSale ? ' mw-tier--not-on-sale' : ''}`;

                    const maxPerPerson = tier.max_ticket_per_person ?? 0;

                    return (
                        <div key={tier._id} class={tierClass}>
                            <div class="mw-tier-header">
                                <div>
                                    <div class="mw-tier-name">{tier.fee_name}</div>
                                    {maxPerPerson > 0 && (
                                        <div class="mw-tier-max-per-person">Max {maxPerPerson} per person</div>
                                    )}
                                    {tier.description && <div class="mw-tier-desc">{tier.description}</div>}
                                </div>
                                <div class="mw-tier-price">
                                    {discountLabel && original !== effective && (
                                        <span class="mw-tier-price--original">
                                            {formatCurrency(original, tier.currency)}
                                        </span>
                                    )}
                                    {isFreeLocked ? (
                                        <span style={{ color: 'var(--mw-text-muted)' }}>🔒 Free</span>
                                    ) : showServerAllIn ? (
                                        <span class="mw-tier-price--all-in">{tier.fee_preview!.formatted_total}</span>
                                    ) : effective === 0 ? (
                                        'Free'
                                    ) : (
                                        formatCurrency(effective, tier.currency)
                                    )}
                                    {discountLabel && !isFreeLocked && !showServerAllIn && (
                                        <div
                                            style={{ fontSize: '0.7rem', color: 'var(--mw-success)', fontWeight: 600 }}>
                                            {discountLabel}
                                        </div>
                                    )}
                                    {(() => {
                                        const line = formatFeePreviewInclusions(tier.fee_preview);
                                        if (!line) return null;
                                        return <div class="mw-tier-fee-preview">{line}</div>;
                                    })()}
                                </div>
                            </div>

                            {tier.fee_features.length > 0 && (
                                <ul class="mw-tier-features">
                                    {tier.fee_features.map(f => (
                                        <li key={f} class="mw-tier-feature">
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {widget?.show_remaining_tickets && tier.available > 0 && tier.available <= 20 && (
                                <div class="mw-tier-remaining">Only {tier.available} left</div>
                            )}

                            {isFree && (
                                <div style={{ marginTop: '0.75rem' }}>
                                    <SponsorThumbnailCard tier={tier} unlocked={sponsorUnlocked} />
                                </div>
                            )}

                            {hasSponsor && !isFree && !sponsorUnlocked && (
                                <div
                                    style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--mw-primary)',
                                        marginTop: '0.5rem',
                                        textAlign: 'center',
                                    }}>
                                    🎬 Sponsor discount available at checkout
                                </div>
                            )}

                            <div class="mw-tier-footer">
                                <div style={{ marginLeft: 'auto' }}>
                                    {soldOut ? (
                                        <span style={{ fontSize: '0.8rem', color: 'var(--mw-error)' }}>Sold out</span>
                                    ) : !onSale ? (
                                        <span style={{ fontSize: '0.8rem', color: 'var(--mw-text-muted)' }}>
                                            Not on sale
                                        </span>
                                    ) : isFreeLocked ? null : (
                                        <QuantityControl
                                            value={qty}
                                            max={maxQty}
                                            onChange={q =>
                                                dispatch({ type: 'UPDATE_QUANTITY', tierId: tier._id, quantity: q })
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {cartCount > 0 && (
                <div class="mw-cart-summary">
                    {cart.map(c => {
                        const isUnlocked = !!unlockedSponsorTiers[c.tier._id];
                        const { effective } = getDisplayPrice(c.tier, isUnlocked);
                        const fp = c.tier.fee_preview;
                        const lineCents = getTicketLineSubtotalCents(c, unlockedSponsorTiers);
                        const useAllInLabel =
                            !!fp?.formatted_total && fp?.total_with_fees != null && effective === c.tier.fee;
                        return (
                            <div key={c.tier._id} class="mw-cart-line">
                                <span>
                                    {c.quantity}× {c.tier.fee_name}
                                </span>
                                <span>
                                    {useAllInLabel && c.quantity === 1
                                        ? fp.formatted_total
                                        : formatCurrency(lineCents, c.tier.currency)}
                                </span>
                            </div>
                        );
                    })}
                    {discount && (
                        <div class="mw-cart-line">
                            <span class="mw-discount-badge">
                                🏷{' '}
                                {discount.type === 'percentage'
                                    ? `-${discount.value}%`
                                    : `-${formatCurrency(discount.value, currency)}`}
                            </span>
                            <span style={{ color: 'var(--mw-success)' }}>
                                −
                                {discount.type === 'percentage'
                                    ? `${discount.value}%`
                                    : formatCurrency(discount.value, currency)}
                            </span>
                        </div>
                    )}
                    <div
                        class="mw-cart-line"
                        style={{ borderTop: '1px solid var(--mw-border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                        <span>Subtotal</span>
                        <span>{formatCurrency(prePromoCents, currency)}</span>
                    </div>
                    {useFeePreviewTotals
                        ? null
                        : prePromoCents > 0 && (
                              <div class="mw-cart-line">
                                  <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>
                                      Service Fees
                                  </span>
                                  <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>
                                      {state.calculatingFees ? '...' : formatCurrency(fees, currency)}
                                  </span>
                              </div>
                          )}
                    {useFeePreviewTotals || discount
                        ? null
                        : prePromoCents > 0 &&
                          tax > 0 && (
                              <div class="mw-cart-line">
                                  <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>Tax</span>
                                  <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>
                                      {state.calculatingFees ? '...' : formatCurrency(tax, currency)}
                                  </span>
                              </div>
                          )}
                    {(useFeePreviewTotals || discount) && (
                        <p
                            class="mw-cart-fee-preview-note"
                            style={{ fontSize: '0.75rem', color: 'var(--mw-text-muted)', margin: '0.25rem 0 0' }}>
                            {discount
                                ? 'Discount applies to ticket price only; per-ticket fees and tax are unchanged'
                                : 'Subtotal and total include all fees and tax per ticket'}
                        </p>
                    )}
                    <div class="mw-cart-total">
                        <span>Total</span>
                        <span>
                            {state.calculatingFees && !useFeePreviewTotals && !discount
                                ? '...'
                                : formatCurrency(grandTotalCents, currency)}
                        </span>
                    </div>
                    {checkoutError && (
                        <p
                            style={{
                                color: 'var(--mw-error, #b92902)',
                                fontSize: '0.85rem',
                                marginBottom: '0.5rem',
                                textAlign: 'center',
                            }}>
                            {checkoutError}
                        </p>
                    )}
                    <div style={{ marginTop: '0.75rem' }}>
                        <button
                            type="button"
                            class="mw-btn mw-btn--primary mw-btn--block"
                            onClick={handleContinue}
                            disabled={eventClosed}>
                            {widget?.custom_cta_text || 'Get Tickets'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
